import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import '../styles/Common.css';
import { Table, Button } from "antd";
import HSOrderDetailModal from '../components/HSOrderDetailModal';
import { v4 as uuidv4 } from "uuid";
import { buildApiUrl } from '../config/api';

// ------------ 기본 클래스 정의 ------------

/**
 * 주문 클래스 - 주문 정보를 관리하는 객체
 */
class Order {
  constructor(no, address, recipient, orderCode, itemList) {
    this.no = no;                // 주문 번호
    this.address = address;      // 배송 주소
    this.recipient = recipient;  // 수취인
    this.itemList = itemList;    // 상품 목록
    this.orderCode = orderCode;  // 주문 코드
    this.isError = false;        // 오류 여부 플래그
    this.parcel = '택배2';       // 기본 배송 방식
  }
}

/**
 * 아이템 클래스 - 개별 상품 정보를 관리하는 객체
 */
class Item {
  constructor(no, itemCode, itemName = '', quantity = '', color = '', handle = '', 
              width = '', height = '', type = '', option = '', note = '') {
    this.no = no;              // 아이템 번호 (UUID)
    this.itemCode = itemCode;  // 아이템 코드
    this.itemName = itemName;  // 상품명
    this.quantity = quantity;  // 수량
    this.color = color;        // 색상 코드
    this.handle = handle;      // 손잡이 위치 (좌/우)
    this.width = width;        // 너비
    this.height = height;      // 높이
    this.type = type;          // 상품 타입 (blind, curtain, honey 등)
    this.option = option;      // 옵션 정보 문자열
    this.note = note;          // 비고
    this.fabicCode = '';       // 원단 코드 (API 조회 결과로 설정)
    this.sashCode = '';        // 샷시 코드 (API 조회 결과로 설정)
  }
}

const HanssemMallPage = () => {
  // ------------ 상태 관리 ------------
  
  // 모달 관련 상태
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [orderDetail, setOrderDetail] = useState(null);
  
  // 데이터 관련 상태
  const [orders, setOrders] = useState([]);           // 처리된 주문 목록
  const [error, setError] = useState(null);           // 오류 메시지
  const [options, setOptions] = useState([]);         // 옵션 마스터 데이터
  const [subCodes, setSubCodes] = useState([]);       // 서브코드 마스터 데이터
  
  // UI 관련 상태
  const [showDownloadButton, setShowDownloadButton] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [fileName, setFileName] = useState('');

  // ------------ 테이블 설정 ------------
  
  /**
   * 주문 목록 테이블 컬럼 정의
   */
  const OrderTablecolumns = [
    {
      title: '주문번호',
      dataIndex: 'orderCode',
      key: 'orderCode',
      align: "center",
    },
    {
      title: '수취인',
      dataIndex: 'recipient',
      key: 'recipient',
      align: "center",
    },
    {
      title: '자세히 보기',
      align: "center",
      render: (_, record) => (
        <Button type="primary" onClick={() => handleDetail(record)}>자세히 보기</Button>
      )
    }
  ];

  // ------------ 이벤트 핸들러 ------------
  
  /**
   * 주문 상세보기 모달 표시
   * @param {Object} record - 주문 데이터
   */
  const handleDetail = (record) => {
    setOrderDetail(record);
    setIsModalVisible(true);
  };

  /**
   * 모달 닫기 및 데이터 업데이트
   * @param {Object} updatedData - 수정된 주문 데이터
   */
  const handleModalClose = (updatedData) => {
    if (updatedData) {
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.orderCode === updatedData.orderCode
            ? { ...order, itemList: updatedData.itemList }
            : order
        )
      );
    }
    setIsModalVisible(false);
  };

  /**
   * 테이블 표시 토글
   */
  const toggleTable = () => {
    setShowTable((prevShowTable) => !prevShowTable);
  };

  // ------------ 데이터 로드 및 처리 ------------
  
  /**
   * 기초 데이터 로드 (옵션 및 서브코드)
   */
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [optionsRes, subCodesRes] = await Promise.all([
          axios.get(buildApiUrl('/api/hs_option')),
          axios.get(buildApiUrl('/api/hs_subcodes')),
        ]);
        setOptions(optionsRes.data);
        setSubCodes(subCodesRes.data);
      } catch (error) {
        setError('기초 데이터를 가져오는 중 오류가 발생했습니다.');
        console.error('초기 데이터 로드 오류:', error);
      }
    };
    fetchInitialData();
  }, []);

  // ------------ API 호출 함수 ------------
  
  /**
   * 아이템 세부 정보를 API에서 조회
   * @param {Array} itemCodes - 조회할 아이템 코드 목록
   * @returns {Promise<Array>} - 아이템 세부 정보
   */
  const fetchItemDetails = async (itemCodes) => {
    try {
      const { data } = await axios.post(buildApiUrl('/api/hs_code'), { codes: itemCodes });
      return data;
    } catch (error) {
      setError('아이템 정보를 가져오는 중 오류가 발생했습니다.');
      console.error('아이템 정보 조회 오류:', error);
      return [];
    }
  };

  /**
   * 색상 코드에 해당하는 원단 정보를 API에서 조회
   * @param {Array} colorCodes - 조회할 색상 코드 목록
   * @returns {Promise<Array>} - 원단 세부 정보
   */
  const fetchColor = async (colorCodes) => {
    try {
      const { data } = await axios.post(buildApiUrl('/api/hs_fabric'), { codes: colorCodes });
      return data;
    } catch (error) {
      setError('원단 정보를 가져오는 중 오류가 발생했습니다.');
      console.error('원단 정보 조회 오류:', error);
      return [];
    }
  };

  // ------------ 데이터 처리 함수 ------------
  
  /**
   * 코드 목록으로부터 아이템 객체 생성
   * @param {Array} codes - 상품 코드 목록
   * @param {Array} subCodes - 서브코드 마스터 데이터
   * @param {Array} options - 옵션 마스터 데이터
   * @param {Array} row - 엑셀 행 데이터
   * @returns {Array} - 생성된 아이템 객체 목록
   */
  const generateItemsFromCodes = (codes, subCodes, options, row) => {
    let newList = [];
    
    // 수량 및 코드 데이터 정리
    if (codes.length > 1) {
      codes.forEach((code, index) => {
        if (index === 0) {
          // 첫 번째 코드는 기본 수량 사용
          codes[0] = { code: codes[0], quantity: row[9] };
        } else {
          // 코드에 수량이 포함된 경우 (예: 'CODE*3') 파싱
          if (code.includes('*')) {
            const [part, quantity] = code.split('*');
            codes[index] = { code: part, quantity: quantity };
          } else {
            codes[index] = { code: code, quantity: 1 };
          }
        }
      });
    } else {
      // 단일 코드인 경우
      codes[0] = { code: codes[0], quantity: row[9] };
    }
    
    // 1단계: 서브코드와 일치하는 아이템 처리
    codes = codes.filter((codedata) => {
      const matchingSubCode = subCodes.find(subCode => subCode.code === codedata.code);
      if (matchingSubCode) {
        // 서브코드 매칭 시 해당 정보로 아이템 생성
        const item = new Item(
          uuidv4(), 
          codedata.code, 
          matchingSubCode.name, 
          codedata.quantity, 
          '', '좌', '0', '0', 
          '서브아이템', 
          row[6],
          row[12]
        );
        item.fabicCode = matchingSubCode.fabric;
        item.sashCode = "*기본";
        newList.push(item);
        return false; // 필터링 제외
      }
      return true; // 필터링 유지
    });
    
    // 2단계: 옵션과 일치하지 않는 아이템 처리
    const hasMatchingOptions = codes.some(codedata => 
      options.some(option => option.code === codedata.code)
    );
    
    if (!hasMatchingOptions) {
      // 옵션 매칭 없는 코드는 그대로 아이템으로 생성
      codes.forEach(codedata => {
        newList.push(
          new Item(uuidv4(), codedata.code, '', codedata.quantity, '', '', '', '', '', row[6], row[12])
        );
      });
      codes = [];
    }

    // 3단계: 옵션과 일치하는 아이템 처리
    let deleteItem;
    codes.forEach((codedata, index) => {
      const matchingOption = options.find(option => option.code === codedata.code);
      if (matchingOption) {
        codes.splice(index, 1);
        deleteItem = matchingOption;
      }
    });

    // 4단계: 남은 코드와 삭제된 옵션 아이템 조합
    if (deleteItem) {
      codes.forEach((remainingCode) => {
        newList.push(
          new Item(
            uuidv4(), 
            `${remainingCode.code}/${deleteItem.code}`, 
            '', 
            remainingCode.quantity, 
            '', '', '', '', '', 
            row[6], 
            row[12]
          )
        );
      });
    }
    
    return newList;
  };

  /**
   * 엑셀 행 데이터를 주문 객체로 변환
   * @param {Array} rows - 엑셀 행 데이터
   * @param {Array} subCodes - 서브코드 마스터 데이터
   * @param {Array} options - 옵션 마스터 데이터
   * @returns {Array} - 주문 객체 배열
   */
  const parseRowsToOrders = (rows, subCodes, options) => {
    // 주소별 주문 데이터 그룹화
    const addressMap = new Map();

    // 첫 번째 행(헤더)은 제외하고 처리
    rows.slice(1).forEach(row => {
      // 주소 포맷팅
      const address = `${row[12] || ''}/${row[15] || ''}(${row[11] || ''})`;
      // 콤마로 구분된 코드 목록 파싱
      const codes = row[4]?.split(',').map(code => code.trim()) || [];
      // 주문 코드 설정
      const orderCode = row[2].split('-')[0] + '-1';

      // 해당 코드로 아이템 생성
      const items = generateItemsFromCodes(codes, subCodes, options, row);
      
      // 동일 주소는 하나의 주문으로 그룹화
      if (!addressMap.has(address)) {
        addressMap.set(address, { 
          recipient: row[12], 
          orderCode, 
          itemList: [] 
        });
      }
      // 해당 주소의 주문에 아이템 추가
      addressMap.get(address).itemList.push(...items);
    });

    // Map을 주문 객체 배열로 변환
    return Array.from(addressMap, ([address, details], idx) => 
      new Order(idx + 1, address, details.recipient, details.orderCode, details.itemList)
    );
  };

  /**
   * 아이템 세부 정보를 업데이트
   * @param {Array} parsedOrders - 기본 주문 객체 배열
   */
  const updateItemDetails = async (parsedOrders) => {
    // 아이템 코드 추출 (서브아이템 제외, 중복 제거)
    const itemCodes = [...new Set(
      parsedOrders.flatMap(order =>
        order.itemList
          .filter(item => item.type.trim().toLowerCase() !== '서브아이템')
          .map(item => item.itemCode)
      )
    )];

    // 아이템 세부 정보 조회
    const itemDetails = await fetchItemDetails(itemCodes);
    
    // 아이템 정보 업데이트
    const updatedOrders = parsedOrders.map(order => ({
      ...order,
      itemList: order.itemList.map(item => {
        // 서브아이템은 처리 제외
        if (item.type.trim().toLowerCase() === '서브아이템') {
          return item;
        }
        
        // 아이템 코드와 일치하는 상세 정보 찾기
        const detail = itemDetails.find(d => d.code === item.itemCode);
        
        // 기본 정보 업데이트
        const updatedItem = {
          ...item,
          itemName: detail ? detail.name : item.itemCode,
          type: detail ? detail.type : 'unknown',
        };
        
        // 상품 유형별 처리
        if (updatedItem.type.trim().toLowerCase() === 'blind') {
          // 블라인드 상품 처리
          processBlindItem(updatedItem, detail);
        } 
        else if (updatedItem.type.trim().toLowerCase() === 'honey') {
          // 허니콤 상품 처리
          processHoneycombItem(updatedItem, detail);
        } 
        else if (updatedItem.type.trim().toLowerCase() === 'curtain') {
          // 커튼 상품 처리
          processCurtainItem(updatedItem, detail);
        } 
        else {
          // 기타 상품 기본 처리
          updatedItem.width = '0';
          updatedItem.height = '0';
        }
        
        return updatedItem;
      }),
    }));

    // 색상 코드 추출 (중복 제거)
    const colorCodes = [...new Set(
      updatedOrders.flatMap(order =>
        order.itemList
          .filter(item => item.color) // color 값이 있는 아이템만
          .map(item => item.color)
      )
    )];
    
    try {
      // 원단 정보 조회
      const fabricDetails = await fetchColor(colorCodes);
      
      // 원단 정보 업데이트
      const finalOrders = updatedOrders.map(order => ({
        ...order,
        itemList: order.itemList.map(item => {
          if (item.color) {
            const fabricDetail = fabricDetails.find(detail => detail.code === item.color);
            return {
              ...item,
              fabicCode: fabricDetail ? fabricDetail.fabric : `알수없는 색상 코드 (${item.color})`,
              sashCode: fabricDetail ? fabricDetail.sash : `알수없는 색상 코드 (${item.color})`,
            };
          }
          return item; // color 값이 없는 경우 그대로 유지
        }),
      }));
      
      // 특수 규칙 적용
      CheckCurtain(finalOrders);
      CheckDoubleRail(finalOrders);
      CheckSubItem(finalOrders);
      CheckParcel(finalOrders);
      CheckErrorOrder(finalOrders);
      
      // 상태 업데이트
      setOrders(finalOrders);
      setShowDownloadButton(true);
      setShowResult(true);
    } 
    catch (fetchColorError) {
      setError('원단 정보를 업데이트하는 중 오류가 발생했습니다.');
      console.error('원단 정보 업데이트 오류:', fetchColorError);
    }
  };

  // ------------ 상품 유형별 처리 함수 ------------
  
  /**
   * 블라인드 상품 처리
   * @param {Object} item - 처리할 아이템 객체
   * @param {Object} detail - 아이템 상세 정보
   */
  const processBlindItem = (item, detail) => {
    const option = item.option.split('/');
    
    // 옵션 타입에 따른 처리
    if (option[0].includes('색상')) {
      // 색상 옵션 처리
      item.color = detail.code + option[0].split(':')[1].replace(/\s+/g, '');
      
      // 손잡이 위치 처리
      if (option[1].includes('우')) {
        item.handle = '우';
      } else if (option[1].includes('좌')) {
        item.handle = '좌';
      } else if (option[1].includes('없음')||option[1].includes('노코드')) {
        item.handle = '좌';
      }

      // 크기 처리
      item.width = option[option.length-2].split(':')[1].trim() * 10;
      item.height = option[option.length-1].split(':')[1].trim() * 10;
    } 
    else if (option[0].includes('스타일') || option[0].includes('원단')) {
      // 스타일/원단 옵션 처리
      item.color = detail.code + option[0].split(':')[1].replace(/\s+/g, '') + 
                   option[1].split(':')[1].replace(/\s+/g, '');
      
      // 손잡이 위치 처리
      if (option[2].includes('우')) {
        item.handle = '우';
      } else if (option[2].includes('좌')) {
        item.handle = '좌';
      } else if (option[2].includes('없음')) {
        item.handle = '좌';
      }
      
      // 크기 처리
      item.width = option[4].split(':')[1].trim() * 10;
      item.height = option[5].split(':')[1].trim() * 10;
    } 
    else if (option[0].includes('슬랫')) {
      // 슬랫 옵션 처리
      item.handle = "슬랫";
      
      // 라운드 타입 처리
      if (option[0].includes('라운드')) {
        item.note = '★우드 라운드' + item.note;
      }

      // 색상 처리
      if (option[1].includes('색상')) {
        item.color = detail.code + option[1].split(':')[1].replace(/\s+/g, '');
        
        // 손잡이 위치 처리
        if (option[1].includes('우')) {
          item.handle = '우';
        } else if (option[2].includes('좌')) {
          item.handle = '좌';
        }
        
        // 크기 처리
        item.width = option[3].split(':')[1].trim() * 10;
        item.height = option[4].split(':')[1].trim() * 10;
      }
    }
  };

  /**
   * 허니콤 상품 처리
   * @param {Object} item - 처리할 아이템 객체
   * @param {Object} detail - 아이템 상세 정보
   */
  const processHoneycombItem = (item, detail) => {
    const option = item.option.split('/');
    
    // 색상 처리
    item.color = detail.code.split('/')[0] + option[0].split(':')[1].replace(/\s+/g, '');
    
    // 손잡이 위치 처리
    if (option[1].includes('우')) {
      item.handle = '우';
    } else if (option[1].includes('좌')) {
      item.handle = '좌';
    } else {
      item.handle = '좌';
    }
    
    // 특수 유형 처리
    if (item.itemName.includes('탑다운')) {
      item.note = '세미오토탑다운/' + item.note;
    } else if (item.itemName.includes('반자동')) {
      item.note = '세미오토/' + item.note;
    }
    
    // 크기 처리
    item.width = option[2].split(':')[1].trim() * 10;
    item.height = option[3].split(':')[1].trim() * 10;
  };

  /**
   * 커튼 상품 처리
   * @param {Object} item - 처리할 아이템 객체
   * @param {Object} detail - 아이템 상세 정보
   */
  const processCurtainItem = (item, detail) => {
    const option = item.option.split('/');
    
    // 색상 처리 - 공백 및 형상기억가공 문구 제거
    item.color = detail.code + option[0].split(':')[1]
                 .replace(/\s+/g, '')
                 .replace('(형상기억가공)', '');
    
    // 크기 처리 - 정규식으로 치수 추출
    item.width = item.option.match(/가로(\d+)cm/)?.[1] * 10;
    item.height = item.option.match(/세로(\d+)cm/)?.[1] * 10;
    
    // 비고에 크기 정보 추가
    item.note += `/${item.width}*${item.height}`;
    
    // 형상기억가공 표시
    if (option[0].includes('형상기억가공')) {
      item.note = '★SSL/' + item.note;
    }
    
    // Double 커튼 특수 처리
    if (item.itemName.includes('Double')||item.itemName.includes('SSL')) {
      item.width = item.quantity * 600;
      item.handle = '좌';
      item.quantity = 1;
    }
    // Plain 커튼 특수 처리
    if (item.itemName.includes('Plain')) {
      item.width = item.quantity;
      item.handle = '좌';
      item.quantity = 1;
    }
  };

  // ------------ 특수 규칙 적용 함수 ------------
  
  /**
   * 배송 방법 검사 및 수정
   * @param {Array} orders - 주문 객체 배열
   */
  const CheckParcel = (orders) => {
    // 지역별 시공 주문 배송방법 변경
    orders.forEach(order => {
      if (order.parcel === '시공') {
        // 지역별 배송 방법 설정
        if (order.address.includes('부산광역시')) {
          const splitAddress = order.address.split('/');
          order.address = '★보관료선불-울산학성지점(다온디자인 052-296-1471)/' + 
                          splitAddress[splitAddress.length-1];
          order.parcel = '화물2';
        } 
        else if (order.address.includes('울산광역시')) {
          const splitAddress = order.address.split('/');
          order.address = '★보관료선불-광안지점(010.3122.7155)/' + 
                          splitAddress[splitAddress.length-1];
          order.parcel = '화물2';
        }
        else if (order.address.includes('대구광역시')) {
          order.parcel = '택배2';
        }
        else if (order.address.includes('서울특별시') || 
                order.address.includes('경기') || 
                order.address.includes('인천광역시')) {
          order.parcel = '납품';
        }
      }
    });
    
    // 각 아이템 비고에 주소 정보 추가
    orders.forEach(order => {
      for (let item of order.itemList) {
        if (order.parcel === '화물2' && item.type !== 'cost') {
          item.note += `${order.address.split('/')[0]}/@${order.address.split('/').slice(1).join('/')}`;
        } else {
          if (item.type !== 'cost') {
            item.note += '/' + order.address.split('/').slice(1).join('/');
          }
        }
      }
    });

    // 납품을 제외한 주문에 택배비 추가
    orders.forEach(order => {
      if (order.parcel !== '납품') {
        if (order.address.includes('제주특별')) {
          // 제주는 착불 처리
          order.address = '착불-' + order.address;
           // 택배비 아이템 추가
           // 택배비 아이템 추가
          const parcel = new Item(
            uuidv4(), '', '기타 비용', '1', '', '좌', '0', '0', '', '', 
            `${order.recipient}/${order.orderCode}`
          );
          parcel.fabicCode = '선불택배';
          parcel.sashCode = '*기본';
          parcel.type = 'cost';
          parcel.price = '-0';
          order.itemList.push(parcel);
        } else {
          // 택배비 아이템 추가
          const parcel = new Item(
            uuidv4(), '', '기타 비용', '1', '', '좌', '0', '0', '', '', 
            `${order.recipient}/${order.orderCode}`
          );
          parcel.fabicCode = '선불택배';
          parcel.sashCode = '*기본';
          parcel.type = 'cost';
          order.itemList.push(parcel);
        }
      }
    });
  };

  /**
   * 커튼 특수 처리
   * @param {Array} orders - 주문 객체 배열
   */
  const CheckCurtain = (orders) => {
    orders.forEach((order) => {
      // 커튼 개수 집계
      let curtainCount = 0;
      for (let item of order.itemList) {
        if (item.type === 'curtain') {
          curtainCount += parseInt(item.quantity);
        }
      }
      
      // 커튼이 있는 경우 비고에 개수 표시
      if (curtainCount > 0) {
        for (let item of order.itemList) {
          if (item.type === 'curtain' && curtainCount > 1) {
            item.note += `(${curtainCount})/`;
          }
        }
      }
    });
  };

  /**
   * 이중 레일 특수 처리
   * @param {Array} orders - 주문 객체 배열
   */
  const CheckDoubleRail = (orders) => {
    orders.forEach((order) => {
      for (let item of order.itemList) {
        if (item.fabicCode.includes('이중레일')) {
          item.note += `★${item.fabicCode}-${item.quantity}개`;
        }
      }
    });
  };

  /**
   * 시공비, 출장비 등 특수 항목 처리
   * @param {Array} orders - 주문 객체 배열
   */
  const CheckSubItem = (orders) => {
    orders.forEach((order) => {
      for (let item of order.itemList) {
        if (item.fabicCode.includes('시공비') || item.fabicCode.includes('출장비')) {
          item.note = `${order.recipient}/${order.orderCode}`;
          item.type = 'cost';
          order.parcel = '시공';
        }
      }
    });
  };

  /**
   * 오류 주문 식별
   * @param {Array} orders - 주문 객체 배열
   */
  const CheckErrorOrder = (orders) => {
    orders.forEach((order) => {
      for (let item of order.itemList) {
        // 원단 코드 누락 체크
        if (item.fabicCode.length === 0) {
          order.isError = true;
          break;
        }
        // 알 수 없는 색상 코드 체크
        if (item.fabicCode && item.fabicCode.includes("알수없는")) {
          order.isError = true;
          break;
        }
      }
    });
  };

  // ------------ 파일 처리 함수 ------------
  
  /**
   * 엑셀 파일 다운로드
   */
  const downloadExcel = () => {
    // 워크시트 헤더 설정
    const worksheetData = [
      ["공장구분", "배송구분", "배송주소", "제품명", "원단코드", "제품단가", "기본단위", 
       "샷시코드", "가로", "세로", "수량(좌)", "수량(우)", "분할방식", "분할값", 
       "추가금액", "할인율", "금액조정", "VAT여부", "손잡이", "타입", "줄길이", 
       "공장지시", "배송사항", "비고", "실사코드", "위치연번"]
    ];

    // 주문 데이터 추가
    orders.forEach(order => {
      order.itemList.forEach(item => {
        worksheetData.push([
          "본사", 
          order.parcel, 
          order.address, 
          item.itemName, 
          item.fabicCode, 
          item?.price ?? '', 
          "", 
          item.sashCode, 
          item.width, 
          item.height, 
          item.handle === '좌' ? item.quantity : '0', 
          item.handle === '우' ? item.quantity : '0', 
          "", 
          "", 
          "", 
          "", 
          "", 
          "", 
          "", 
          "", 
          "1", 
          "", 
          "", 
          item.note, 
          "", 
          ""
        ]);
      });
    });

    // 워크시트 생성
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // 스타일 적용
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!worksheet[cellAddress]) continue;
        
        worksheet[cellAddress].s = {
          alignment: { horizontal: "left", vertical: "center" },
          border: { 
            top: { style: "thin" }, 
            bottom: { style: "thin" }, 
            left: { style: "thin" }, 
            right: { style: "thin" } 
          },
        };
      }
    }

    // 워크북 생성 및 저장
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");
    XLSX.writeFile(workbook, "orders.xlsx");
  };

  /**
   * 엑셀 파일 업로드 처리
   * @param {File} file - 업로드된 엑셀 파일
   */
  const handleFileUpload = (file) => {
    if (!file) return;
    
    setFileName(file.name);
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        // 엑셀 파일 파싱
        const rows = XLSX.utils.sheet_to_json(
          XLSX.read(new Uint8Array(e.target.result), { type: 'array' }).Sheets['Sheet1'],
          { header: 1 }
        );
        
        // 데이터 검증
        if (rows.length < 2) {
          return setError('데이터가 비어있습니다.');
        }
        
        // 주문 객체 생성 및 처리
        const parsedOrders = parseRowsToOrders(rows, subCodes, options);
        await updateItemDetails(parsedOrders);

        setError(null);
      } catch(error) {
        setError('파일을 처리하는 중 오류가 발생했습니다.');
        console.error('파일 처리 오류:', error);
      }
    };
    
    reader.readAsArrayBuffer(file);
  };

  // ------------ 렌더링 부분 ------------
  
  return (
    <>
      <div className="common-container">
        <h1>한샘몰 페이지</h1>
        
        {/* 파일 업로드 영역 */}
        <div
          className="file-drop-zone"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            handleFileUpload(e.dataTransfer.files[0]);
          }}
          style={{ 
            border: '2px dashed #ccc', 
            padding: '20px', 
            textAlign: 'center', 
            marginBottom: '20px' 
          }}
        >
          <p>여기에 파일을 드래그하세요 또는 아래 버튼을 클릭하여 업로드하세요.</p>
          <input
            type="file"
            accept=".xlsx, .xls"
            id="file-upload"
            style={{ display: 'none' }}
            onChange={(e) => handleFileUpload(e.target.files[0])}
          />
          <label
            htmlFor="file-upload"
            style={{ 
              cursor: 'pointer', 
              padding: '10px 20px', 
              background: '#007BFF', 
              color: '#fff', 
              borderRadius: '5px' 
            }}
          >
            파일 선택
          </label>
          <br/>
          
          {/* 다운로드 버튼 */}
          {showDownloadButton && (
            <button 
              onClick={downloadExcel} 
              style={{ 
                marginTop: '20px', 
                padding: '10px 20px', 
                background: '#28a745', 
                color: '#fff', 
                borderRadius: '5px', 
                cursor: 'pointer' 
              }}
            >
              엑셀 다운로드
            </button>
          )}
        </div>
        
        {/* 오류 메시지 */}
        {error && <p style={{ color: 'red' }}>{error}</p>}
      </div>
      
      {/* 결과 표시 영역 */}
      {showResult && (
        <div className="common-container">
          <div style={{ marginBottom: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <label style={{ marginRight: "10px" }}>파일 명 : {fileName}</label>
              <button style={{ padding: "5px 10px" }} onClick={toggleTable}>
                {showTable ? "주문 리스트 숨기기" : "주문 리스트 보이기"}
              </button>
            </div>
            <label style={{ display: 'block', marginTop: '10px' }}>주문 개수 : {orders.length}</label>
            <label style={{ display: 'block', marginTop: '10px' }}>
              주문 오류 개수 : {orders.filter(o => o.isError === true).length}
            </label>
          </div>
          
          {/* 주문 테이블 */}
          {showTable && (
            <div>
              <Table 
                columns={OrderTablecolumns} 
                dataSource={orders} 
                pagination={false}
                rowClassName={(record) => (record.isError ? "highlight-error-row" : "")}
                rowKey="orderCode"
              />
            </div>
          )}
        </div>
      )}
      
      {/* 주문 상세 모달 */}
      <HSOrderDetailModal
        isVisible={isModalVisible}
        onClose={handleModalClose}
        orderDetail={orderDetail}
      />
    </>
  );
};

export default HanssemMallPage;